import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import QuoteForm from '@/components/QuoteForm';

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      {/* Hero with Gradient */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(220,38,38,0.15)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(220,38,38,0.1)_0%,_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-12">
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl">
            Ready to clear out your junk? Contact us for a free estimate or call us directly.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-10">
                {/* Phone */}
                <a 
                  href="tel:9183210654"
                  className="flex items-center gap-4 p-4 bg-[#f5f5f5] rounded-xl hover:bg-[#dc2626] hover:text-white group transition-colors"
                  data-testid="contact-phone"
                >
                  <div className="w-14 h-14 bg-[#dc2626] group-hover:bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white group-hover:text-[#dc2626]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 group-hover:text-white/70">Phone</p>
                    <p className="font-bold text-lg">(918) 321-0654</p>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href="mailto:support@junkremovalmd.com"
                  className="flex items-center gap-4 p-4 bg-[#f5f5f5] rounded-xl hover:bg-[#1a1a1a] hover:text-white group transition-colors"
                  data-testid="contact-email"
                >
                  <div className="w-14 h-14 bg-[#1a1a1a] group-hover:bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white group-hover:text-[#1a1a1a]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 group-hover:text-white/70">Email</p>
                    <p className="font-bold">support@junkremovalmd.com</p>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-center gap-4 p-4 bg-[#f5f5f5] rounded-xl">
                  <div className="w-14 h-14 bg-gray-300 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#1a1a1a]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-bold text-[#1a1a1a]">Tahlequah, Oklahoma</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-center gap-4 p-4 bg-[#f5f5f5] rounded-xl">
                  <div className="w-14 h-14 bg-gray-300 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#1a1a1a]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hours</p>
                    <p className="font-bold text-[#1a1a1a]">8 A.M. – 8 P.M.</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mb-10">
                <h3 className="font-semibold text-[#1a1a1a] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/JunkRemovalMD/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-[#1877f2] text-white rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-5 h-5" />
                    <span className="font-semibold">Facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/junkremovalmd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="contact-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="font-semibold">Instagram</span>
                  </a>
                </div>
              </div>

              {/* Quick Tip */}
              <div className="bg-[#dc2626]/10 border border-[#dc2626]/20 p-6 rounded-xl">
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#dc2626] mb-2">
                  Fastest Way to Get a Quote
                </h3>
                <p className="text-gray-700 mb-4">
                  Text photos of your junk to <span className="font-bold">(918) 321-0654</span> with your name and service address. We'll get back to you with an estimate quickly!
                </p>
                <a href="sms:9183210654">
                  <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white font-bold btn-press" data-testid="contact-text-btn">
                    Text Us Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Quote Form */}
            <div>
              <div className="bg-[#f5f5f5] p-8 md:p-10 rounded-2xl">
                <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#1a1a1a] mb-2">
                  Request a Free Estimate
                </h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
                <QuoteForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder / Service Area Reminder */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 md:px-12 text-center">
          <p className="text-gray-400 mb-4">
            Serving Cherokee County and Northeast Oklahoma
          </p>
          <p className="text-white font-['Barlow_Condensed'] text-2xl font-bold">
            Not sure if we service your area? Give us a call!
          </p>
        </div>
      </section>
    </div>
  );
}
